import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/cn";

export type ButtonVariant = "default" | "primary" | "accent" | "quiet";
export type ButtonSize = "default" | "small";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  default: "",
  primary: "button-primary",
  accent: "button-accent",
  quiet: "button-quiet",
};

/**
 * 原型基元：`.button`（+ `.button-primary/.button-accent/.button-quiet/.button-small`）。
 * 交互态（hover / active / disabled）全部由 baseline.css 提供，组件不再覆盖。
 */
export function Button({
  variant = "default",
  size = "default",
  className,
  type = "button",
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        "button",
        VARIANT_CLASS[variant],
        size === "small" && "button-small",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
}

/** 原型基元：`.icon-button`。必须传 `label`，用于无障碍名称。 */
export function IconButton({
  label,
  className,
  type = "button",
  children,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      className={cn("icon-button", className)}
      aria-label={label}
      title={label}
      {...rest}
    >
      {children}
    </button>
  );
}
